import React from 'react';
import { borderRadius } from '../theme/theme';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error | null }>;
}

// Default fallback component - uses inline styles to avoid theme dependency
// This ensures it works even if ThemeProvider crashes
const DefaultErrorFallback: React.FC<{ error: Error | null }> = ({ error }) => {
  const fallbackStyles = {
    background: 'var(--pipeex-background-default)',
    surface: 'var(--pipeex-background-paper)',
    text: 'var(--pipeex-text-primary)',
    secondaryText: 'var(--pipeex-text-secondary)',
    error: 'var(--pipeex-error-main)',
    primary: 'var(--pipeex-primary-main)',
    border: 'color-mix(in srgb, var(--pipeex-text-secondary) 32%, transparent)',
  };

  return (
    <div
      style={{
        backgroundColor: fallbackStyles.background,
        minHeight: '100vh',
        boxSizing: 'border-box',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        color: fallbackStyles.text
      }}
    >
      <main
        style={{
          width: '100%',
          minHeight: '100vh',
          boxSizing: 'border-box',
          padding: '24px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: 0,
          borderRadius: `${borderRadius.none}px`,
          backgroundColor: fallbackStyles.surface,
          boxShadow: 'none',
          overflow: 'hidden',
        }}
        aria-label="Application error"
        aria-live="assertive"
      >
      <h2 style={{ color: fallbackStyles.error, fontWeight: 600, marginBottom: '16px' }}>
        Something went wrong
      </h2>
      <p style={{ color: fallbackStyles.secondaryText, marginBottom: '24px' }}>
        An unexpected error has occurred. Please try reloading.
      </p>
      {error?.message && (
        <p style={{ color: fallbackStyles.error, fontSize: '14px', marginBottom: '16px' }}>
          {error.message}
        </p>
      )}
      {error?.stack && (
        <details
          style={{
            whiteSpace: 'pre-wrap',
            textAlign: 'left',
            maxWidth: '800px',
            width: '100%',
            backgroundColor: fallbackStyles.surface,
            padding: '16px',
            borderRadius: `${borderRadius.md}px`,
            marginBottom: '24px',
            overflow: 'auto',
            maxHeight: '200px',
            border: `1px solid ${fallbackStyles.border}`
          }}
        >
          <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: '8px' }}>
            Error details
          </summary>
          <pre style={{ color: fallbackStyles.secondaryText, fontSize: '11px', fontFamily: 'monospace', margin: 0 }}>
            {error.stack}
          </pre>
        </details>
      )}
      <button
        onClick={() => window.location.reload()}
        style={{
          marginTop: '8px',
          padding: '12px 24px',
          backgroundColor: fallbackStyles.primary,
          color: 'var(--pipeex-primary-contrast-text)',
          border: 'none',
          borderRadius: `${borderRadius.md}px`,
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500
        }}
      >
        Reload page
      </button>
      </main>
    </div>
  );
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: undefined };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log the error to an error reporting service
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      // Render fallback UI if provided, otherwise render a default error message
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error || null} />;
      }

      return <DefaultErrorFallback error={this.state.error || null} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
